/**
 * use-inbox-filter.ts — Trạng thái "chế độ xem tuỳ chỉnh" + bộ lọc nâng cao của Hộp thư.
 *
 *  - Chế độ xem đang chọn nằm trên URL (`?sv=<id>`) để tải lại / chia sẻ link vẫn giữ.
 *  - Sửa điều kiện ở nút phễu là TẠM: gắn với chế độ xem đang chọn, đổi chế độ
 *    xem (hoặc bộ lọc có sẵn) / tải lại trang là mất, quay về điều kiện gốc.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useInboxViews, type InboxSavedView } from '@/hooks/use-inbox-views'
import {
  cleanConditions,
  conditionsQuery,
  sameConditions,
  type ConditionLogic,
  type InboxCondition,
} from './inbox-conditions'

interface Temp {
  viewId?: string
  conditions: InboxCondition[]
  logic: ConditionLogic
}

const EMPTY: InboxCondition[] = []

export function useInboxFilter() {
  const [params, setParams] = useSearchParams()
  const savedViewId = params.get('sv') ?? undefined
  const { data, isSuccess } = useInboxViews()
  const activeView: InboxSavedView | undefined = data?.views.find((v) => v.id === savedViewId)
  const [temp, setTemp] = useState<Temp | null>(null)

  const baseConditions = activeView?.conditions ?? EMPTY
  const baseLogic: ConditionLogic = activeView?.logic ?? 'and'
  const tempActive = temp !== null && temp.viewId === savedViewId
  const conditions = tempActive ? temp.conditions : baseConditions
  const logic = tempActive ? temp.logic : baseLogic

  const selectView = useCallback(
    (id?: string) => {
      setTemp(null)
      setParams(
        (p) => {
          const next = new URLSearchParams(p)
          if (id) next.set('sv', id)
          else next.delete('sv')
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  // Chế độ xem trên URL không còn (bị xoá / không có quyền) → bỏ, tránh danh sách báo lỗi.
  useEffect(() => {
    if (savedViewId && isSuccess && !activeView) selectView(undefined)
  }, [savedViewId, isSuccess, activeView, selectView])

  const apply = useCallback(
    (next: InboxCondition[], nextLogic: ConditionLogic) => setTemp({ viewId: savedViewId, conditions: next, logic: nextLogic }),
    [savedViewId],
  )

  /**
   * Tham số gửi cho GET /conversations. Chưa tải xong danh sách chế độ xem mà
   * URL đã có `sv` → nhờ backend tự lấy điều kiện (`savedViewId`) để khỏi nháy
   * danh sách không lọc.
   */
  const query = useMemo(() => {
    if (tempActive || activeView) return conditionsQuery(conditions, logic)
    if (savedViewId && !isSuccess) return { savedViewId }
    return {}
  }, [tempActive, activeView, conditions, logic, savedViewId, isSuccess])

  return {
    savedViewId: activeView ? savedViewId : undefined,
    activeView,
    conditions,
    logic,
    baseConditions,
    baseLogic,
    /** Số điều kiện đang áp dụng — hiện trên nút phễu. */
    count: cleanConditions(conditions).length,
    /** Đang sửa tạm khác điều kiện gốc. */
    dirty: tempActive && !sameConditions(temp.conditions, temp.logic, baseConditions, baseLogic),
    query,
    selectView,
    apply,
    reset: () => setTemp(null),
  }
}
